import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './BookStore.css';
import API_BASE_URL, { API_ENDPOINTS } from '../../config/api';
import LogoLoader from '../../components/LogoLoader';
import { getAuthToken } from '../../utils/auth';
import axios from 'axios';

const OwnBookCard = ({ book, onDelete }) => {
    return (
        <div className="book-card relative group flex flex-col items-center overflow-hidden rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
            <button 
                className="own-book-delete-btn opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600" 
                onClick={(e) => {
                    e.preventDefault();
                    if (window.confirm('Are you sure you want to delete this book?')) {
                        onDelete(book._id);
                    }
                }}
                title="Delete Book"
            >
                <i className="fi fi-rr-trash text-sm mt-1"></i>
            </button>
            
            <Link to={`/app-store/bookstore/own-books/read/${book._id}`} className="w-full aspect-[2/3] flex flex-col">
                <div className="book-image-wrapper flex-grow flex items-center justify-center p-6 text-center">
                    <h3 className="text-xl font-medium text-white/90 leading-snug break-words">
                        {book.productName}
                    </h3>
                </div>
            </Link>
        </div>
    );
};

const OwnBooks = () => {
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const token = getAuthToken();
            if (!token) {
                setError('Please sign in to view your library');
                setLoading(false);
                return;
            }

            try {
                const [booksRes, profileRes] = await Promise.all([
                    axios.get(API_ENDPOINTS.OWN_BOOKS.ALL, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(API_ENDPOINTS.USER.PROFILE, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                if (booksRes.data && booksRes.data.success) {
                    setBooks(booksRes.data.data);
                }
                if (profileRes.data && profileRes.data.success) {
                    setIsSubscribed(profileRes.data.data.isSubscribed);
                }
            } catch (err) {
                console.error('Failed to fetch library data:', err);
                setError('Failed to load your personal library.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleDelete = async (id) => {
        const token = getAuthToken();
        try {
            const response = await axios.delete(API_ENDPOINTS.OWN_BOOKS.DELETE(id), {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setBooks(books.filter(b => b._id !== id));
            }
        } catch (err) {
            alert('Failed to delete book');
        }
    };

    const filteredBooks = useMemo(() => {
        let result = books;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = books.filter(book => 
                book.productName.toLowerCase().includes(query) ||
                book.fileName.toLowerCase().includes(query)
            );
        }
        
        // Remove duplicates based on fileName to prevent duplicate cards
        const uniqueBooks = [];
        const seenNames = new Set();
        for (const book of result) {
            if (!seenNames.has(book.productName)) {
                seenNames.add(book.productName);
                uniqueBooks.push(book);
            }
        }
        return uniqueBooks;
    }, [books, searchQuery]);

    if (loading) return <LogoLoader progress={50} />;

    return (
        <div className="bookstore-page">
            <Helmet>
                <title>My Library | Book Store | Hari Vishnu</title>
            </Helmet>

            <div className="bookstore-header">
                <div className="bookstore-header-content">
                    <Link to="/app-store/bookstore" className="text-white/70 hover:text-white flex items-center gap-2 mb-4 transition-colors">
                        <i className="fi fi-rr-arrow-left"></i> Back to Store
                    </Link>
                    <h1 className="bookstore-title">My Personal Library</h1>
                    <p className="bookstore-subtitle">
                        {isSubscribed 
                            ? "Premium: Unlimited storage. Enjoy your private collection." 
                            : `Free Tier: ${books.length}/1 book stored. Upgrade for unlimited.`}
                    </p>
                    
                    <div className="bookstore-search-container">
                        <div className="bookstore-search-bar">
                            <i className="fi fi-rr-search search-icon"></i>
                            <input 
                                type="text" 
                                placeholder="Search your books..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bookstore-search-input"
                            />
                            {searchQuery && (
                                <button className="clear-search" onClick={() => setSearchQuery('')}>
                                    <i className="fi fi-rr-cross-small"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bookstore-container">
                {error ? (
                    <div className="bookstore-error">
                        <i className="fi fi-rr-exclamation"></i>
                        <p>{error}</p>
                        {error.includes('sign in') && (
                            <Link to="/signin" className="read-own-btn add-btn mt-4 inline-flex">Sign In</Link>
                        )}
                    </div>
                ) : filteredBooks.length === 0 ? (
                    <div className="bookstore-empty">
                        <i className="fi fi-rr-book-open-reader"></i>
                        <p>{searchQuery ? `No books found for "${searchQuery}"` : "Your personal library is empty. Upload a book to get started!"}</p>
                    </div>
                ) : (
                    <div className="bookstore-grid">
                        {filteredBooks.map((book) => (
                            <OwnBookCard key={book._id} book={book} onDelete={handleDelete} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OwnBooks;
